/**
 * THE BUTTON NAMES THE TUTORIAL SAYS — one table, read by BOTH the buttons and the guide's copy.
 *
 * A step that says "Open Tools → Plant" is only right while the buttons are called Tools and Plant. So the buttons
 * render their text from here (`label("bar.tools")`), and the copy builds its menu paths from here too
 * (`path("bar.tools", "tools.plant")` → "Tools → Plant"): renaming a button renames it in every instruction that names
 * it, in the same edit. Keys are the guide's anchor ids (src/sim/guide/anchors.ts) where the thing is one, so a step
 * that POINTS at a button and the words that NAME it can never disagree.
 *
 * src/sim/guide.test.ts fails if a label here is not drawn from this table by the UI, or if the guide's content types
 * a menu path ("→") by hand instead of through `path()`.
 */
import type { GuideAnchorId } from "./anchors";

export const LABELS = {
  "nav.greenhouse": "Greenhouse",
  "nav.garden": "Garden",
  "bar.shop": "Market",
  "bar.decorate": "Decorate",
  "bar.inventory": "Inventory",
  "bar.menu": "Menu",
  "bar.tools": "Tools",
  "tools.plant": "Plant",
  "tools.water": "Water",
  "tools.harvest": "Harvest",
  "tools.harvestAll": "Harvest all",
  "bar.build": "Build",
  "build.plots": "Buy / sell plots",
  "build.waterPainter": "Water Painter",
  "bar.fish": "Fishing",
  "fish.rod": "Fishing Rod",
  "fish.tackle": "Tackle",
  "bar.bugs": "Bugs",
  "bugs.net": "Bug Net",
  "bugs.talents": "Bug Catching Talents",
  "menu.achievements": "Achievements",
  "menu.guide": "Field Guide",
  "hud.journal": "Garden Journal",
  // the Market's drop zones the copy names
  "market.buyOne": "Buy one",
  "market.sellOne": "Sell one",
} as const satisfies Partial<Record<GuideAnchorId, string>> & Record<string, string>;

export type LabelId = keyof typeof LABELS;

/** A button's name. */
export function label(id: LabelId): string { return LABELS[id]; }

/** A menu path as the player reads it: `path("bar.tools", "tools.plant")` → "Tools → Plant". */
export function path(...ids: LabelId[]): string { return ids.map(label).join(" → "); }
